import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { BannerForm } from "./banner-form";

import { FormError, SideSheet, notify } from "@/components/kit";
import { useCreateBanner } from "@/hooks/api/banner/useCreateBanner";
import {
  BANNER_FORM_SCHEMA,
  BannerFormData,
  EMPTY_BANNER_FORM,
} from "@/schemas/banner-form.schema";
import { getApiErrorMessage } from "@/utils/api-error";
import { buildBannerLink } from "@/utils/banner-link";

interface AddBannerModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AddBannerModal({ isOpen, onClose }: AddBannerModalProps) {
  const form = useForm<BannerFormData>({
    resolver: zodResolver(BANNER_FORM_SCHEMA),
    mode: "all",
    defaultValues: EMPTY_BANNER_FORM,
  });

  const {
    mutate: createBanner,
    isPending: isCreatingBanner,
    error,
    reset: resetMutation,
  } = useCreateBanner();

  useEffect(() => {
    if (!isOpen) return;

    form.reset(EMPTY_BANNER_FORM);
    resetMutation();
  }, [isOpen]);

  const handleSubmit = (data: BannerFormData) => {
    createBanner(
      { imageUrl: data.imageUrl, ...buildBannerLink(data.target) },
      {
        onSuccess: () => {
          notify.success("Banner created");
          onClose();
        },
      },
    );
  };

  return (
    <FormProvider {...form}>
      <SideSheet
        description="Shows on the app and website home page"
        isOpen={isOpen}
        isSubmitting={isCreatingBanner}
        submitLabel="Create banner"
        title="Add banner"
        onClose={onClose}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <FormError
          message={
            error
              ? getApiErrorMessage(
                  error,
                  "An error occurred while creating the banner, please try again!",
                )
              : null
          }
        />
        <BannerForm />
      </SideSheet>
    </FormProvider>
  );
}
